import { useState, useEffect, useCallback } from 'react';
import { superAdminService } from '../../services/api';

const API = process.env.REACT_APP_API_URL || '';

interface QaLogEntry {
    id: string;
    question: string;
    answer: string;
    citations: Array<{ act: string; section?: string; summary?: string }>;
    regulationIds?: string[];
    userId: string;
    userEmail?: string;
    model: string;
    confidence: string;
    createdAt: string;
    recordHash: string;
}

export default function QAHistory() {
    const [logs, setLogs] = useState<QaLogEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selected, setSelected] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const res = await fetch(`${API}/api/qa/history/logs/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const result = await res.json();
            if (result.success) {
                setLogs(Array.isArray(result.data) ? result.data : []);
            } else {
                setError(result.message || 'Failed to fetch logs');
            }
        } catch (err: any) {
            setError(err.message || 'Network error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const filtered = logs.filter(log => {
        const matchSearch = !searchTerm ||
            log.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.userEmail || '').toLowerCase().includes(searchTerm.toLowerCase());

        const logDate = new Date(log.createdAt);
        const matchFrom = !dateFrom || logDate >= new Date(dateFrom);
        const matchTo = !dateTo || logDate <= new Date(dateTo + 'T23:59:59');

        return matchSearch && matchFrom && matchTo;
    });

    const handleExport = () => {
        const rows = filtered.map(log => ({
            Date: new Date(log.createdAt).toLocaleString(),
            Question: log.question,
            Answer: log.answer?.substring(0, 200),
            Citations: (log.citations || []).map(c => c.act).join('; '),
            Confidence: log.confidence,
            User: log.userEmail || log.userId
        }));

        const csv = [
            Object.keys(rows[0] || {}).join(','),
            ...rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', `qa-history-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;
        const rows = filtered.map(log => `
            <tr>
                <td>${new Date(log.createdAt).toLocaleString()}</td>
                <td>${log.question}</td>
                <td>${log.answer?.substring(0, 300)}</td>
                <td>${(log.citations || []).map(c => c.act).join(', ')}</td>
                <td>${log.confidence}</td>
                <td>${log.userEmail || log.userId}</td>
            </tr>
        `).join('');
        printWindow.document.write(`
            <html><head><title>Q&A History Export</title>
            <style>body{font-family:sans-serif;padding:20px}
            table{width:100%;border-collapse:collapse;margin-top:20px}
            th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:12px}
            th{background:#f5f5f5;font-weight:bold}
            tr:nth-child(even){background:#fafafa}
            </style></head><body>
            <h1>Q&A History Export</h1>
            <p>Generated: ${new Date().toLocaleString()} | Entries: ${filtered.length}</p>
            <table>
                <thead><tr><th>Date</th><th>Question</th><th>Answer</th><th>Citations</th><th>Confidence</th><th>User</th></tr></thead>
                <tbody>${rows}</tbody>
            </table>
            <script>window.onload=function(){window.print()};</script>
            </body></html>
        `);
        printWindow.document.close();
    };

    return (
        <div>
            <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">
                            <i className="fas fa-history text-emerald-600 mr-2"></i>
                            Q&A Audit History ({filtered.length})
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Immutable audit trail of all AI Compliance Advisor interactions
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleExport}
                            className="px-3 py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition shadow-sm"
                        >
                            <i className="fas fa-file-export mr-2"></i>CSV
                        </button>
                        <button
                            onClick={handlePrint}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition shadow-sm"
                        >
                            <i className="fas fa-print mr-2"></i>PDF (Print)
                        </button>
                        <button
                            onClick={fetchLogs}
                            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-200 transition"
                        >
                            <i className="fas fa-sync mr-2"></i>Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[200px]">
                    <input
                        type="text"
                        placeholder="Search questions, answers, or users..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-800"
                    />
                </div>
                <div>
                    <label className="text-xs text-gray-500 block mb-1">From</label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs text-gray-500 block mb-1">To</label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                </div>
                {(searchTerm || dateFrom || dateTo) && (
                    <button
                        onClick={() => { setSearchTerm(''); setDateFrom(''); setDateTo(''); }}
                        className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700"
                    >
                        <i className="fas fa-times mr-1"></i>Clear
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-500 border-t-transparent"></div>
                    <span className="ml-3 text-gray-500">Loading Q&A history...</span>
                </div>
            ) : error ? (
                <div className="p-12 text-center">
                    <i className="fas fa-exclamation-triangle text-red-400 text-4xl mb-3"></i>
                    <p className="text-red-600">{error}</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                    <i className="fas fa-comment-slash text-gray-300 text-5xl mb-4"></i>
                    {logs.length === 0
                        ? <p>No Q&A interactions recorded yet. Ask a question in Compliance Advisor to see it here.</p>
                        : <p>No logs match your filter criteria.</p>}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Question</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Answer</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Citations</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Confidence</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">SHA-256</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filtered.map(log => (
                                <tr
                                    key={log.id}
                                    className={`hover:bg-gray-50 cursor-pointer ${selected === log.id ? 'bg-emerald-50' : ''}`}
                                    onClick={() => setSelected(selected === log.id ? null : log.id)}
                                >
                                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                        {new Date(log.createdAt).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                                        {log.question}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 max-w-sm truncate">
                                        {log.answer?.substring(0, 150)}
                                        {(log.answer?.length || 0) > 150 && '...'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {(log.citations || []).map((c, i) => (
                                                <span key={i} className="inline-block bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                                    {c.act}{c.section ? ` §${c.section}` : ''}
                                                </span>
                                            ))}
                                            {(log.citations || []).length === 0 && (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                                            log.confidence === 'HIGH' ? 'bg-emerald-100 text-emerald-700' :
                                            log.confidence === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {log.confidence}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[120px] truncate">
                                        {log.userEmail || log.userId}
                                    </td>
                                    <td className="px-4 py-3">
                                        <code className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">
                                            {log.recordHash?.substring(0, 12)}...
                                        </code>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <div className="border-t border-gray-200 bg-gray-50">
                    {(() => {
                        const log = filtered.find(l => l.id === selected);
                        if (!log) return null;
                        return (
                            <div className="p-6 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-bold text-gray-900 text-sm">Full Details — {log.id}</h3>
                                    <span className="px-2 py-0.5 text-xs font-bold rounded bg-green-100 text-green-700">
                                        <i className="fas fa-shield-check mr-1"></i>SHA-256: {log.recordHash?.substring(0, 16)}...
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <label className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Question</label>
                                        <p className="mt-1 text-sm text-gray-900">{log.question}</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <label className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Answer</label>
                                        <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{log.answer}</p>
                                    </div>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <label className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                                        Citations ({log.citations?.length || 0})
                                    </label>
                                    <div className="mt-2 space-y-2">
                                        {(log.citations || []).length === 0 && (
                                            <p className="text-sm text-gray-400 italic">No citations</p>
                                        )}
                                        {(log.citations || []).map((c, i) => (
                                            <div key={i} className="flex items-start gap-2 text-sm">
                                                <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap">
                                                    {c.act}
                                                </span>
                                                {c.section && (
                                                    <span className="text-gray-500 font-mono text-xs mt-0.5">{c.section}</span>
                                                )}
                                                {c.summary && (
                                                    <span className="text-gray-600 text-xs mt-0.5">{c.summary}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {log.regulationIds && log.regulationIds.length > 0 && (
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <label className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                                            Linked Regulations
                                        </label>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {log.regulationIds.map(rid => (
                                                <span key={rid} className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs font-medium">
                                                    <i className="fas fa-gavel mr-1"></i>
                                                    {rid}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <details className="bg-white rounded-lg p-4 border border-gray-200">
                                    <summary className="text-xs text-gray-500 font-semibold uppercase tracking-wider cursor-pointer">
                                        Technical Details
                                    </summary>
                                    <div className="mt-2 space-y-1 text-xs text-gray-600 font-mono">
                                        <p>ID: {log.id}</p>
                                        <p>Record Hash: {log.recordHash}</p>
                                        <p>Model: {log.model}</p>
                                        <p>Created: {log.createdAt}</p>
                                        <p>User: {log.userEmail || log.userId}</p>
                                    </div>
                                </details>
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}
